import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import ArrowRightIcon from 'components/icons/ArrowRight';
import styles from './styles.module.scss';
import premiumHeroImage from '../../../images/shared/complete-system/mobile-app.png';
import walletHeroImage from './images/apple-wallet-hero.png';
import premiumFaceHeroImage from './images/premium-face-unlock-hero.png';

const APP_STORE_URL = 'https://apps.apple.com/us/app/swiftlane-id/id1465204078';
const OPEN_URL_BASE = '/mobile/wallet-campaign';
const PAGE_PATH = '/mobile/wallet-campaign';
const REQUIRED_PARAMS = [
  'send_log_id',
  'campaign_id',
  'campaign_version',
  'lane_id',
  'workspace_name',
  'event_token',
];
const PRESERVED_PARAMS = [
  'send_log_id',
  'campaign_id',
  'campaign_version',
  'variant_id',
  'lane_id',
  'workspace_name',
  'event_token',
  'feature_group',
  'entry_point',
  'gated_features',
];

const LANES = {
  wallet: 'wallet',
  premium: 'premium',
};

const DEFAULT_LANE_ID = LANES.wallet;

const WALLET_VARIANTS = {
  wallet_hero_no_app_ritual: {
    headline: 'Skip the app at the door',
    body: 'Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at supported Swiftlane doors. No app to open.',
    benefits: [
      'After setup, tap in without opening the Swiftlane app.',
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
      'Your key is stored securely on your iPhone or Apple Watch.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_hands_full: {
    headline: 'Tap in when your hands are full',
    body: 'Add your key to Apple Wallet, then tap your iPhone or Apple Watch at supported Swiftlane doors. Nothing to dig out.',
    benefits: [
      'Use iPhone or Apple Watch instead of a fob, PIN, or app unlock.',
      'Keep moving when your hands are full or you are in a hurry.',
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_power_reserve: {
    headline: 'A key that outlasts your battery',
    body: 'Add your key to Apple Wallet, then tap in at supported Swiftlane doors. With Power Reserve, eligible iPhones can tap when battery runs out.',
    benefits: [
      'When your iPhone battery is low, Power Reserve can help your key keep working for up to five hours on eligible iPhones.',
      'Tap in without opening the Swiftlane app after setup.',
      'Avoid low-battery worry on late nights, busy days, and trips home.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_easy_home: {
    headline: 'Make coming home easier',
    body: "Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at your building's Swiftlane doors.",
    benefits: [
      'Come home with one quick tap at the door.',
      'Use iPhone or Apple Watch instead of a face scan, fob, PIN, or app unlock.',
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_secure_simple: {
    headline: 'A simpler, secure way in',
    body: "Add your key to Apple Wallet, then tap in at your building's Swiftlane doors. Protected like your cards, no app and no code.",
    benefits: [
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
      'Your key is stored securely on your iPhone or Apple Watch.',
      'If your device is lost, Wallet access can be turned off.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_watch_pockets: {
    headline: 'Open the door with Apple Watch',
    body: "Add your key to Apple Watch, then tap in at your building's Swiftlane doors, even on a run or when your phone is upstairs.",
    benefits: [
      'Step out for runs, walks, and quick trips with less to carry.',
      'Use Apple Watch instead of a face scan, fob, PIN, or app unlock.',
      'After setup, tap with Apple Watch without opening the Swiftlane app.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_watch_routine: {
    headline: 'Put your key on Apple Watch',
    body: "For runs, walks, and quick trips out, add your key to Apple Wallet and tap back in with Apple Watch at your building's Swiftlane doors.",
    benefits: [
      'Use Apple Watch instead of a face scan, fob, PIN, or app unlock.',
      'Your key is stored securely on your iPhone or Apple Watch.',
      'After setup, tap with Apple Watch without opening the Swiftlane app.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_finish_setup: {
    headline: 'Finish adding your Wallet key',
    body: "Pick up where you left off: add your key to Apple Wallet, then tap in with iPhone or Apple Watch at your building's Swiftlane doors.",
    benefits: [
      'Finish now for easier entry the next time you come home.',
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
      'Use iPhone or Apple Watch instead of a face scan, fob, PIN, or app unlock.',
    ],
    primaryCta: 'Finish Wallet Setup',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_trial_use: {
    headline: 'Give the door a tap',
    body: 'Your trial is active. Add your key to Apple Wallet, then hold your iPhone or Apple Watch near the reader at supported doors.',
    benefits: [
      "Try it at your building's Swiftlane doors while your trial is active.",
      'Use iPhone or Apple Watch instead of a face scan, fob, PIN, or app unlock.',
      'Your key is stored securely on your iPhone or Apple Watch.',
    ],
    primaryCta: 'Try Tap Access',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_trial_winback: {
    headline: 'Turn tap access back on',
    body: 'Your trial ended, so tap access is off. Turn it back on, then tap in with iPhone or Apple Watch at supported Swiftlane doors.',
    benefits: [
      'Bring back the tap access you already tried.',
      'Use iPhone or Apple Watch instead of a face scan, fob, PIN, or app unlock.',
      'Review details first; nothing changes until you turn it on.',
    ],
    primaryCta: 'Turn Tap Access Back On',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_value_everyday: {
    headline: 'Every entry, one tap',
    body: 'Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at the supported Swiftlane doors you use every day.',
    benefits: [
      'Save a few seconds every time you come home.',
      'Use iPhone or Apple Watch instead of a face scan, fob, PIN, or app unlock.',
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_app_reactivation: {
    headline: 'Set up your Apple Wallet key',
    body: 'Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at supported Swiftlane doors. A free trial is included.',
    benefits: [
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
      'Use iPhone or Apple Watch instead of a face scan, fob, PIN, or app unlock.',
      'Your key is stored securely on your iPhone or Apple Watch.',
    ],
    primaryCta: 'Set Up Tap Access',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_tap_everyday: {
    headline: 'Tap in faster',
    body: "Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at your building's Swiftlane doors.",
    benefits: [
      'Use one quick tap at supported doors.',
      'Set up once, then keep your key ready in Apple Wallet.',
      'Your key is stored securely on your iPhone or Apple Watch.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_hands_full_simple: {
    headline: 'Tap in when your hands are full',
    body: "Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at your building's Swiftlane doors.",
    benefits: [
      'Keep moving when your hands are full or you are in a hurry.',
      'Use iPhone or Apple Watch for a quick tap at supported doors.',
      'Your key stays protected in Apple Wallet.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_no_code_entry: {
    headline: 'Tap in instead of typing a code',
    body: "Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at your building's Swiftlane doors, no code to type.",
    benefits: [
      'No door code to remember or type.',
      'Tap in with iPhone or Apple Watch at supported doors.',
      'Your key is stored securely on your iPhone or Apple Watch.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_private_key: {
    headline: 'Use a private key instead of a PIN',
    body: 'Turn on your key in Apple Wallet, then use a private key instead of a code to type at supported doors.',
    benefits: [
      'Use a personal key instead of a code to type.',
      'Your key is stored securely on your iPhone or Apple Watch.',
      'Review details first; nothing changes until you turn it on.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_no_fob_entry: {
    headline: "A key that's always with you",
    body: 'Add your key to Apple Wallet, then tap in with iPhone or Apple Watch even on days your fob stays home.',
    benefits: [
      'Forgot your fob? Use iPhone or Apple Watch to tap in anyway.',
      'Use the phone or Apple Watch you already carry.',
      'Your key is stored securely on your iPhone or Apple Watch.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_backup_key: {
    headline: 'Make your phone your everyday key',
    body: 'Add your key to Apple Wallet, then tap in with iPhone or Apple Watch as your everyday way in at supported doors.',
    benefits: [
      'Make iPhone or Apple Watch your everyday key at supported doors.',
      'Use Apple Wallet instead of reaching for your fob.',
      'When your iPhone battery is low, Power Reserve can help your key keep working for up to five hours on eligible iPhones.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_review_wallet_access: {
    headline: 'Add your key to Apple Wallet',
    body: "Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at your building's Swiftlane doors. A free trial is included.",
    benefits: [
      'See setup and pricing before making changes.',
      "Use iPhone or Apple Watch at your building's Swiftlane doors after setup.",
      'Nothing changes until you turn it on.',
    ],
    primaryCta: 'Review Wallet Access',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_everyday_value: {
    headline: 'A faster key for everyday entry',
    body: 'Review Apple Wallet access, then use iPhone or Apple Watch for the doors you use most.',
    benefits: [
      'Use Wallet for the entries you make all week.',
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
      'Review setup and pricing before making changes.',
    ],
    primaryCta: 'Review Wallet Access',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_one_tap_entry: {
    headline: 'One tap at the door',
    body: "Add your key to Apple Wallet, then tap in with iPhone or Apple Watch at your building's Swiftlane doors.",
    benefits: [
      'Use one quick tap at supported doors.',
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
      'Your key is stored securely on your iPhone or Apple Watch.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_turn_key_back_on: {
    headline: 'Turn your Wallet key back on',
    body: "Reactivate your key in Apple Wallet, then tap in with iPhone or Apple Watch at your building's Swiftlane doors.",
    benefits: [
      'Bring back the tap access you already tried.',
      'Reactivate Apple Wallet access for supported doors.',
      'Review details first; nothing changes until you turn it on.',
    ],
    primaryCta: 'Turn Tap Access Back On',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_phone_key: {
    headline: 'Keep your key on your phone',
    body: 'Add your key to Apple Wallet so it is ready on iPhone or Apple Watch when your fob is not handy.',
    benefits: [
      'Use iPhone or Apple Watch instead of reaching for a fob.',
      'Make iPhone or Apple Watch your everyday key at supported doors.',
      'When your iPhone battery is low, Power Reserve can help your key keep working for up to five hours on eligible iPhones.',
    ],
    primaryCta: 'Add to Apple Wallet',
    storeCta: 'Get the Swiftlane app',
  },
  wallet_hero_wallet_available_review: {
    headline: 'Wallet access is available',
    body: 'Open Swiftlane to review Apple Wallet setup for supported doors.',
    benefits: [
      'Review setup when you are ready.',
      'Your key in Apple Wallet lets you tap without waking or unlocking your device after setup.',
      'Nothing changes until you turn it on.',
    ],
    primaryCta: 'Review Wallet Access',
    storeCta: 'Get the Swiftlane app',
  },
};

const FALLBACK_VARIANT_ID = 'wallet_hero_no_app_ritual';

const PREMIUM_FEATURE_CLAIMS = {
  FACE_UNLOCK: 'Unlock hands-free with Face Unlock where your property supports it.',
  WALLET_ACCESS: 'Add your resident key to Apple Wallet or Google Wallet where available.',
  SHOW_PIN_USAGE_HISTORY: 'See when PIN access was used and whether entry was granted.',
  SHOW_INTERCOM_HISTORY: 'Review intercom call history and visitor snapshots when available.',
  LIVE_VIEW: 'Check the latest door photo before you decide what to do.',
  INTERCOM_CALL_BACK: 'Start a video call to the intercom from the Swiftlane app.',
};

const PREMIUM_GENERIC_BENEFITS = [
  'Review the Premium features available at your property.',
  'See plan details before making any changes.',
  'Open Swiftlane with the same resident account to continue.',
];

const PREMIUM_VARIANTS = {
  // Review fix: keep fallback benefit order aligned with iOS/cloud Premium routing
  // so email, app, and web fallback show the same first benefits.
  premium_face_unlock: {
    headline: 'Walk up, the door opens',
    body: 'Premium adds Face Unlock so supported doors open as you walk up, plus the other gated features at your property.',
    primaryCta: 'See Premium',
    featureOrder: ['FACE_UNLOCK', 'WALLET_ACCESS', 'SHOW_PIN_USAGE_HISTORY'],
  },
  premium_wallet_access: {
    headline: 'A key for your iPhone',
    body: 'Premium can add Wallet access so you tap in with iPhone or Apple Watch, plus the other gated features at your property.',
    primaryCta: 'Add Wallet access',
    featureOrder: ['WALLET_ACCESS', 'FACE_UNLOCK', 'SHOW_PIN_USAGE_HISTORY'],
  },
  premium_heavy_pin_user: {
    headline: 'See every time your PIN was used',
    body: 'Premium can add PIN access history and the other gated features available at your property.',
    primaryCta: 'See Premium',
    featureOrder: ['SHOW_PIN_USAGE_HISTORY', 'WALLET_ACCESS', 'FACE_UNLOCK'],
  },
  premium_heavy_keycard_fob_user: {
    headline: 'Use your phone, not the fob',
    body: 'Premium can add phone and watch entry at supported doors, even on days the fob stays home.',
    primaryCta: 'Add Wallet access',
    featureOrder: ['WALLET_ACCESS', 'FACE_UNLOCK', 'SHOW_PIN_USAGE_HISTORY'],
  },
  premium_heavy_mobile_unlock_user: {
    headline: 'Quicker ways to unlock',
    body: 'Premium adds Face Unlock, Wallet taps, and Live View where supported, plus the other gated features at your property.',
    primaryCta: 'See Premium',
    featureOrder: ['FACE_UNLOCK', 'WALLET_ACCESS', 'LIVE_VIEW'],
  },
  premium_visibility_history: {
    headline: 'See what happened while you were out',
    body: 'Premium shows access history, intercom activity, and door photos where available at your property.',
    primaryCta: 'See Premium',
    featureOrder: ['SHOW_PIN_USAGE_HISTORY', 'SHOW_INTERCOM_HISTORY', 'LIVE_VIEW'],
  },
  premium_live_view_call_door: {
    headline: 'Check the door from the couch',
    body: 'Premium adds Live View to see the latest door photo and call the intercom from Swiftlane, where available.',
    primaryCta: 'See Premium',
    featureOrder: ['LIVE_VIEW', 'INTERCOM_CALL_BACK', 'SHOW_INTERCOM_HISTORY'],
  },
  premium_bundle: {
    headline: 'One plan for your building',
    body: 'Premium brings together the gated features your building supports, from Face Unlock to Wallet keys, in one plan.',
    primaryCta: 'See Premium',
    featureOrder: ['FACE_UNLOCK', 'WALLET_ACCESS', 'SHOW_PIN_USAGE_HISTORY'],
  },
  premium_finish_upgrade: {
    headline: 'Finish your Premium upgrade',
    body: 'Your upgrade is still waiting. Come back to turn on the gated features available at your property.',
    primaryCta: 'Finish upgrade',
    featureOrder: ['FACE_UNLOCK', 'WALLET_ACCESS', 'SHOW_PIN_USAGE_HISTORY'],
  },
};

const FALLBACK_PREMIUM_VARIANT_ID = 'premium_bundle';

const CAMPAIGN_RENDERING_BY_LANE = {
  [LANES.wallet]: {
    variants: WALLET_VARIANTS,
    fallbackVariantId: FALLBACK_VARIANT_ID,
    eyebrow: 'Apple Wallet Access',
    fallbackNote:
      'If Swiftlane does not open, install the app and sign in with the same resident account. Availability depends on your building and supported Swiftlane readers.',
    heroImage: walletHeroImage,
    imageAlt: 'Swiftlane Wallet access',
  },
  [LANES.premium]: {
    variants: PREMIUM_VARIANTS,
    fallbackVariantId: FALLBACK_PREMIUM_VARIANT_ID,
    eyebrow: 'Swiftlane Premium',
    fallbackNote:
      'If Swiftlane does not open, install the app and sign in with the same resident account. Premium availability depends on your building and resident plan.',
    // Per-variant hero by each email's primary selling point (matches the email
    // campaign): the face-recognition photo for the face-unlock pitch, the reused
    // Apple Wallet hero ONLY for the Wallet-pitch variants (so Wallet artwork
    // never sits beside non-Wallet copy — the prior review concern), and the
    // generic Premium hero for the rest. `heroImage` is the no-override default.
    heroImage: premiumHeroImage,
    heroImageByVariant: {
      premium_face_unlock: premiumFaceHeroImage,
      premium_bundle: premiumFaceHeroImage,
      premium_finish_upgrade: premiumFaceHeroImage,
      premium_wallet_access: walletHeroImage,
      premium_heavy_keycard_fob_user: walletHeroImage,
      premium_heavy_mobile_unlock_user: walletHeroImage,
      // Real in-app feature screenshots hosted on the SendGrid CDN (match the
      // per-variant email hero images).
      premium_heavy_pin_user:
        'https://cdn.mcauto-images-production.sendgrid.net/843c64ee15b56fbe/3a9feabd-2d55-40fa-8d51-437993fd1e79/1239x2616.png',
      premium_visibility_history:
        'https://cdn.mcauto-images-production.sendgrid.net/843c64ee15b56fbe/0998c0c3-1c44-48da-9601-488d3e3be3aa/1239x2616.png',
      premium_live_view_call_door:
        'https://cdn.mcauto-images-production.sendgrid.net/843c64ee15b56fbe/75f69c8a-eb1f-4649-85f1-8a813876bfb4/1239x2616.png',
    },
    imageAlt: 'Swiftlane Premium access',
  },
};

// Pick the hero image for the resolved variant: a per-variant override (by
// selling point) when the lane defines one, else the lane default.
function heroImageForState(state) {
  const rendering = state?.rendering;
  if (!rendering) return walletHeroImage;
  const override = rendering.heroImageByVariant?.[state?.variantId];
  return override || rendering.heroImage || walletHeroImage;
}

const CheckIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="m5 12 4 4L19 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function getParams() {
  if (typeof window === 'undefined') return {};
  const sp = new URLSearchParams(window.location.search);
  const params = {};
  sp.forEach((value, key) => {
    params[key] = value;
  });
  return params;
}

function normalizeLaneId(rawLaneId) {
  const normalized = typeof rawLaneId === 'string' ? rawLaneId.trim().toLowerCase() : '';
  if (normalized === LANES.premium) return LANES.premium;
  return DEFAULT_LANE_ID;
}

function parseGatedFeatures(rawGatedFeatures) {
  if (typeof rawGatedFeatures !== 'string') return [];
  return rawGatedFeatures
    .split(',')
    .map((feature) => feature.trim().toUpperCase())
    .filter(Boolean);
}

function premiumBenefitsForVariant(variant, params) {
  const gatedFeatures = parseGatedFeatures(params.gated_features);
  const marketableFeatures = gatedFeatures.filter((feature) => PREMIUM_FEATURE_CLAIMS[feature]);
  const orderedFeatures = [
    ...variant.featureOrder.filter((feature) => marketableFeatures.includes(feature)),
    ...marketableFeatures.filter((feature) => !variant.featureOrder.includes(feature)),
  ];
  const selectedFeatures = orderedFeatures.slice(0, 3);

  if (selectedFeatures.length > 0) {
    return selectedFeatures.map((feature) => PREMIUM_FEATURE_CLAIMS[feature]);
  }

  return PREMIUM_GENERIC_BENEFITS;
}

function resolveVariant(params) {
  const laneId = normalizeLaneId(params.lane_id);
  const rendering = CAMPAIGN_RENDERING_BY_LANE[laneId];
  const rawVariantId = params.variant_id;
  const knownVariant = rawVariantId ? rendering.variants[rawVariantId] : null;
  const usedFallback = !knownVariant && Boolean(rawVariantId);
  const id = knownVariant ? rawVariantId : rendering.fallbackVariantId;
  const baseVariant = knownVariant || rendering.variants[rendering.fallbackVariantId];
  const variant =
    laneId === LANES.premium
      ? {
          ...baseVariant,
          benefits: premiumBenefitsForVariant(baseVariant, params),
          storeCta: 'Get the Swiftlane app',
        }
      : baseVariant;

  if (knownVariant) {
    return { id, variant, laneId, rendering, usedFallback: false };
  }
  return {
    id,
    variant,
    laneId,
    rendering,
    usedFallback,
  };
}

function requiredParamProblems(params, variantResolution) {
  const invalid = REQUIRED_PARAMS.filter(
    (name) => typeof params[name] !== 'string' || params[name].trim() === ''
  );
  if (variantResolution.usedFallback || !params.variant_id) {
    invalid.push('variant_id');
  }
  return invalid;
}

function appendKnownParams(url, params) {
  if (typeof window === 'undefined') return url;
  const isAbsolute = /^https?:\/\//i.test(url);
  const result = new URL(url, window.location.origin);
  PRESERVED_PARAMS.forEach((name) => {
    if (typeof params[name] === 'string' && params[name].trim() !== '') {
      result.searchParams.set(name, params[name]);
    }
  });
  if (isAbsolute) return result.toString();
  return result.pathname + result.search + result.hash;
}

async function sha256Hex(value) {
  if (typeof window === 'undefined' || !window.crypto?.subtle || !window.TextEncoder) return '';
  const bytes = new window.TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function computeEventTokenHash(params) {
  if (!params.campaign_id || !params.campaign_version || !params.event_token) return '';
  return sha256Hex(`${params.campaign_id}:${params.campaign_version}:${params.event_token}`);
}

function userAgentFamily() {
  if (typeof window === 'undefined') return 'other';
  const ua = window.navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Macintosh/i.test(ua)) return 'mac';
  if (/Windows/i.test(ua)) return 'windows';
  return 'other';
}

function track(eventName, properties) {
  if (typeof window === 'undefined') return;
  if (window.analytics && typeof window.analytics.track === 'function') {
    window.analytics.track(eventName, properties);
    return;
  }
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: eventName, wallet_campaign: properties });
  }
}

function buildBaseProperties(state, routingResult, extra) {
  const { params } = state;
  const properties = {
    send_log_id: params.send_log_id || '',
    campaign_id: params.campaign_id || '',
    campaign_version: params.campaign_version || '',
    variant_id: state.variantId,
    requested_variant_id: params.variant_id || '',
    lane_id: state.laneId,
    requested_lane_id: params.lane_id || '',
    workspace_name: params.workspace_name || '',
    event_token_hash: state.eventTokenHash || '',
    source_channel: 'email',
    routing_result: routingResult,
    page_path: PAGE_PATH,
    has_required_params: state.invalidParamNames.length === 0,
    invalid_param_names: state.invalidParamNames.join(','),
    app_handoff_method: 'universal_link',
    user_agent_family: userAgentFamily(),
  };

  properties.event_token_hash_status = state.eventTokenHash ? 'available' : 'unavailable';

  if (params.feature_group) {
    properties.feature_group = params.feature_group;
  }
  if (params.entry_point) {
    properties.entry_point = params.entry_point;
  }
  if (params.gated_features) {
    properties.gated_features = params.gated_features;
  }

  if (extra) {
    Object.keys(extra).forEach((key) => {
      properties[key] = extra[key];
    });
  }

  return properties;
}

const WalletCampaign = () => {
  const [state, setState] = useState(null);

  const isResolvingVariant = !state;
  const variant = state?.variant;
  const variantId = state?.variantId || 'resolving';

  const openUrl = useMemo(
    () => (state ? appendKnownParams(OPEN_URL_BASE, state.params) : OPEN_URL_BASE),
    [state]
  );
  const storeUrl = useMemo(
    () => (state ? appendKnownParams(APP_STORE_URL, state.params) : APP_STORE_URL),
    [state]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = getParams();
      const variantResolution = resolveVariant(params);
      const invalidParamNames = requiredParamProblems(params, variantResolution);

      if (cancelled) return;

      const initialState = {
        params,
        laneId: variantResolution.laneId,
        variantId: variantResolution.id,
        variant: variantResolution.variant,
        rendering: variantResolution.rendering,
        invalidParamNames,
        eventTokenHash: '',
        usedVariantFallback: variantResolution.usedFallback,
      };
      // Apply URL-selected copy before hashing so users never see fallback copy flash first.
      setState(initialState);

      const eventTokenHash = await computeEventTokenHash(params);

      if (cancelled) return;

      const trackedState = {
        ...initialState,
        eventTokenHash,
      };
      setState(trackedState);

      if (invalidParamNames.length > 0) {
        track(
          'wallet_campaign_web_invalid_params',
          buildBaseProperties(
            trackedState,
            variantResolution.usedFallback
              ? 'unknown_variant_fallback_rendered'
              : 'invalid_params_rendered'
          )
        );
      }

      track(
        'wallet_campaign_web_page_viewed',
        buildBaseProperties(
          trackedState,
          invalidParamNames.length > 0 ? 'invalid_params_rendered' : 'web_fallback_rendered'
        )
      );
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handlePrimaryClick = useCallback(
    (event) => {
      event.preventDefault();
      const targetUrl = openUrl;
      if (state) {
        const properties = buildBaseProperties(state, 'universal_link_attempted', {
          cta_type: 'open_swiftlane',
        });
        track('wallet_campaign_web_cta_clicked', properties);
        track('wallet_campaign_web_app_open_attempted', properties);
      }
      window.setTimeout(() => {
        window.location.href = targetUrl;
      }, 80);
    },
    [openUrl, state]
  );

  const handleSecondaryClick = useCallback(
    (event) => {
      event.preventDefault();
      const targetUrl = storeUrl;
      if (state) {
        const properties = buildBaseProperties(state, 'app_store_clicked', {
          cta_type: 'app_store',
          app_handoff_method: 'app_store',
        });
        track('wallet_campaign_web_cta_clicked', properties);
        track('wallet_campaign_web_app_store_clicked', properties);
      }
      window.setTimeout(() => {
        window.location.href = targetUrl;
      }, 80);
    },
    [storeUrl, state]
  );

  return (
    <section
      className={styles.walletCampaign}
      data-lane-id={state?.laneId}
      data-variant-id={variantId}
    >
      <div className={styles.header}>
        <p className={styles.eyebrow}>{state?.rendering?.eyebrow || 'Swiftlane'}</p>
        {isResolvingVariant ? (
          <div className={styles.resolvingHeader} role="status" aria-live="polite">
            <span className={styles.loadingStatus}>Loading campaign details</span>
            <span className={styles.titlePlaceholder} aria-hidden="true" />
            <span className={styles.ledePlaceholder} aria-hidden="true" />
            <span className={styles.ledePlaceholderShort} aria-hidden="true" />
          </div>
        ) : (
          <>
            <Heading className={styles.title} tag="h1" size="xxl">
              {variant.headline}
            </Heading>
            <p className={styles.lede}>{variant.body}</p>
          </>
        )}
      </div>

      <div className={styles.shell}>
        <div className={styles.copy}>
          {!isResolvingVariant && (
            <ul className={styles.proofList}>
              {variant.benefits.map((text) => (
                <li key={text} className={styles.proofItem}>
                  <span className={styles.check} aria-hidden="true">
                    <CheckIcon />
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          )}

          {isResolvingVariant ? (
            <div className={styles.actions} aria-hidden="true">
              <span className={styles.primaryBtnPlaceholder} />
              <span className={styles.secondaryBtnPlaceholder} />
            </div>
          ) : (
            <div className={styles.actions}>
              <Button
                to={openUrl}
                onClick={handlePrimaryClick}
                theme="accent-primary"
                className={styles.primaryBtn}
              >
                {variant.primaryCta}
                <ArrowRightIcon />
              </Button>
              <a href={storeUrl} onClick={handleSecondaryClick} className={styles.secondaryBtn}>
                <span>{variant.storeCta}</span>
              </a>
            </div>
          )}

          <p className={styles.fallbackNote}>
            {state?.rendering?.fallbackNote ||
              CAMPAIGN_RENDERING_BY_LANE[DEFAULT_LANE_ID].fallbackNote}
          </p>
        </div>

        <div className={styles.visual}>
          {isResolvingVariant ? (
            // Hold a neutral skeleton until the variant resolves so users never see
            // the default hero flash and swap to the resolved image.
            <span className={styles.imagePlaceholder} aria-hidden="true" />
          ) : (
            <img
              className={styles.image}
              src={heroImageForState(state)}
              alt={variant.headline || state.rendering.imageAlt || 'Swiftlane access'}
            />
          )}
        </div>
      </div>

      {/* {state && state.invalidParamNames.length > 0 && (
        <div className={styles.debugPanel} aria-live="polite">
          Campaign attribution is missing or invalid: {state.invalidParamNames.join(', ')}
        </div>
      )} */}
    </section>
  );
};

export default WalletCampaign;
